<template>
  <div class="mt-6 container-fluid">
    <!-- <div class="row min-vh-80"> -->
      <div class="col-12">
        <h3 class="text-center pb-3">Shared With Me</h3>

        <!-- Search and Filter Row -->
        <div class="row align-items-center mb-3">
          <div class="col">
            <div class="input-group-text bg-white border-end-0 border-radius-lg" style="max-width: 400px; height: 40px;">
              <i class="fa fa-search mx-2"></i>
              <input
                v-model="searchQuery"
                placeholder="Search files..."
                class="form-control"
              />
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col d-flex justify-content-end gap-2">
            <div ref="typeDropdownBtn" class="dropdown type-dropdown" @click="toggleTypeDropdown">
              <button class="btn btn-sm btn-outline-shareguard dropdown-toggle" type="button">
                <i :class="selectedTypeIcon + ' me-2'"></i>
                {{ selectedTypeLabel }}
              </button>
              <div v-if="showTypeDropdown" class="dropdown-menu show mt-0" :style="dropdownMenuStyle">
                <div
                  class="dropdown-item d-flex align-items-center"
                  :class="{active: !selectedTypeGroup}"
                  @click.stop="selectType('')"
                >
                  <i class="fa fa-layer-group me-2"></i> All types
                </div>
                <div
                  v-for="type in fileTypeGroups"
                  :key="type.key"
                  class="dropdown-item d-flex align-items-center"
                  :class="{active: selectedTypeGroup === type.key}"
                  @click.stop="selectType(type.key)"
                >
                  <i :class="type.icon + ' me-2'" :style="{color: type.color}"></i>
                  {{ type.label }}
                </div>
              </div>
            </div>
            <div ref="sortDropdownBtn" class="dropdown type-dropdown" @click="toggleSortDropdown">
              <button class="btn btn-sm btn-outline-shareguard dropdown-toggle" type="button">
                <i :class="sortIcon + ' me-2'"></i>
                {{ sortLabel }}
              </button>
              <div v-if="showSortDropdown" class="dropdown-menu show" :style="dropdownMenuStyle">
                <div
                  class="dropdown-item d-flex align-items-center"
                  :class="{active: sortOrder === 'newest'}"
                  @click.stop="selectSort('newest')"
                >
                  <i class="fa fa-arrow-down-a-z me-2"></i> Newest first
                </div>
                <div
                  class="dropdown-item d-flex align-items-center"
                  :class="{active: sortOrder === 'oldest'}"
                  @click.stop="selectSort('oldest')"
                >
                  <i class="fa fa-arrow-up-a-z me-2"></i> Oldest first
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- <p class="lead font-weight-normal opacity-8 pb-4 text-center">Files that have been shared with you by other users.</p> -->
         
        <div v-if="isLoadingSharedFiles" class="d-flex justify-content-center align-items-center" style="min-height: 60vh;">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
        
        <div v-else-if="filteredSharedFiles && filteredSharedFiles.length > 0" class="row">
          <div v-for="(file, index) in filteredSharedFiles" :key="index" class="mt-4 mb-2 col-lg-4 col-md-6">
            <shared-file-card
              :file-id="file.id"
              :title="file.title"
              :date-time="file.datetime"
              :owner="file.owner"
              :file-type="file.fileType"
              :content-type="file.contentType"
              :size="file.size"
              :is-encrypted="file.isEncrypted"
            />
          </div>
        </div>
        <div v-else class="d-flex justify-content-center align-items-center" style="min-height: 60vh;">
          <empty-state-card
            title="No shared files yet"
            subtitle="When someone shares a file with you, it will appear here. You can also share your files with others."
            icon="fa-copy"
            button-text="View My Files"
            path="/myfiles"
          >
          </empty-state-card>
        </div>
      </div>

    <!-- </div> -->
  </div>
</template>

<script>
import SharedFileCard from "./SharedFileCard.vue";
import EmptyStateCard from "./EmptyStateCard.vue";
import setNavPills from "@/assets/js/nav-pills.js";
import { mapState, mapActions, mapGetters } from "vuex";
import { FILE_TYPE_META, determineFileType } from "@/utils/files.js";

export default {
  name: "SharedFileList",
  components: {
    SharedFileCard,
    EmptyStateCard
  },
  data() {
    return {
      searchQuery: '',
      selectedTypeGroup: '',
      showTypeDropdown: false,
      showSortDropdown: false,
      sortOrder: 'newest',
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    ...mapGetters("files", ["sharedFiles", "sharedFilesCount", "isLoadingSharedFiles"]),
    name() {
      return this.user ? this.user.name : 'Unknown User';
    },
    fileTypeGroups() {
      // Only show groups that are present in the current shared files
      const presentGroups = new Set();
      this.sharedFiles.forEach(f => {
        const ext = determineFileType(f.title || f.name || '');
        for (const [group, meta] of Object.entries(FILE_TYPE_META)) {
          if (meta.extensions.includes(ext)) {
            presentGroups.add(group);
            break;
          }
        }
      });
      // Always include 'other' if any file doesn't match
      if (this.sharedFiles.some(f => {
        const ext = determineFileType(f.title || f.name || '');
        return !Object.values(FILE_TYPE_META).some(meta => meta.extensions.includes(ext));
      })) {
        presentGroups.add('other');
      }
      // Return as array of { key, ...meta }
      return Object.entries(FILE_TYPE_META)
        .filter(([group]) => presentGroups.has(group))
        .map(([key, meta]) => ({ key, ...meta }));
    },
    selectedTypeLabel() {
      if (!this.selectedTypeGroup) return 'All types';
      return FILE_TYPE_META[this.selectedTypeGroup]?.label || 'All types';
    },
    selectedTypeIcon() {
      if (!this.selectedTypeGroup) return 'fa fa-layer-group';
      return FILE_TYPE_META[this.selectedTypeGroup]?.icon || 'fa fa-layer-group';
    },
    dropdownMenuStyle() {
      return {};
    },
    sortLabel() {
      return this.sortOrder === 'newest' ? 'Newest first' : 'Oldest first';
    },
    sortIcon() {
      return this.sortOrder === 'newest' ? 'fa fa-arrow-down-a-z' : 'fa fa-arrow-up-a-z';
    },
    filteredSharedFiles() {
      let files = this.sharedFiles;
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        files = files.filter(f =>
          f.title.toLowerCase().includes(q) ||
          (f.fileType && f.fileType.toLowerCase().includes(q))
        );
      }
      if (this.selectedTypeGroup) {
        const groupMeta = FILE_TYPE_META[this.selectedTypeGroup];
        files = files.filter(f => {
          const ext = determineFileType(f.title || f.name || '');
          return groupMeta.extensions.includes(ext) || (this.selectedTypeGroup === 'other' && !Object.values(FILE_TYPE_META).some(meta => meta.extensions.includes(ext)));
        });
      }
      // Sort files by upload date
      return files.sort((a, b) => {
        const dateA = this.parseDateTime(a.datetime);
        const dateB = this.parseDateTime(b.datetime);
        return this.sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
      });
    }
  },
  beforeUnmount() {
  },
  async mounted() {
    setNavPills();
    this.fetchSharedFilesAction();
  },
  methods: {
    ...mapActions("files", ["fetchSharedFilesAction"]),
    ...mapActions("snackbar", ["showSnackbar"]),

    removeFile(fileId) {
      //TODO - remove file from sharedFiles with network call
      this.files = this.sharedFiles.filter(file => file.fileId !== fileId);
      this.showSnackbar( { title: "File deleted successfully!", color: "success" });
    },
    toggleTypeDropdown() {
      this.showTypeDropdown = !this.showTypeDropdown;
    },
    toggleSortDropdown() {
      this.showSortDropdown = !this.showSortDropdown;
    },
    selectSort(order) {
      this.sortOrder = order;
      this.showSortDropdown = false;
    },
    parseDateTime(dtStr) {
      // Expects format: 'DD/MM/YYYY HH:mm:ss'
      if (!dtStr) return new Date(0);
      const [datePart, timePart] = dtStr.split(' ');
      if (!datePart || !timePart) return new Date(0);
      const [day, month, year] = datePart.split('/').map(Number);
      const [hour, minute, second] = timePart.split(':').map(Number);
      return new Date(year, month - 1, day, hour, minute, second);
    },
    selectType(group) {
      if (!group) {
        this.selectedTypeGroup = '';
      } else {
        this.selectedTypeGroup = group;
      }
      this.showTypeDropdown = false;
    },
    handleClickOutside(event) {
      if (this.showTypeDropdown && !this.$refs.typeDropdownBtn.contains(event.target)) {
        this.showTypeDropdown = false;
      }
      if (this.showSortDropdown && !this.$refs.sortDropdownBtn.contains(event.target)) {
        this.showSortDropdown = false;
      }
    },
  },
};
</script>

<style scoped>
.type-dropdown {
  position: relative;
  display: inline-block;
}
.type-dropdown .dropdown-menu {
  min-width: 220px;
  box-shadow: 0 4px 16px rgba(60,60,60,0.15);
  border-radius: 12px;
  padding: 0.5rem 0;
  z-index: 1000;
  position: absolute;
}
.type-dropdown .dropdown-item {
  cursor: pointer;
  padding: 0.5rem 1.25rem;
  font-size: 1rem;
  border: none;
  background: none;
}
.type-dropdown .dropdown-item.active,
.type-dropdown .dropdown-item:hover {
  background: #f1f3f4;
}
</style>
